import React from 'react';
import { Helmet } from 'react-helmet';

/**
 * SEO Optimized Component for Lighthouse 100/100 SEO Score
 */
const SEOOptimized = ({
  title = 'Hotel & Restaurant La Casa - Luxury Accommodation in Hattingen',
  description = 'Experience luxury at Hotel & Restaurant La Casa in Hattingen. Perfect blend of comfort, elegance, and authentic Italian culinary excellence.',
  keywords = 'hotel Hattingen, luxury accommodation Germany, Italian restaurant, La Casa, business hotel, elegant dining',
  image = '/og-image.jpg',
  url = 'https://lacasa-hotel.de',
  type = 'website',
  locale = 'en_US',
  siteName = 'Hotel & Restaurant La Casa',
  twitterHandle = '@lacasahotel'
}) => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Hotel',
        '@id': `${url}/#hotel`,
        name: 'Hotel La Casa',
        description: 'Luxury hotel in Hattingen offering elegant accommodation and exceptional service.',
        url: url,
        telephone: '+49232456650',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Heggerstraße 4',
          addressLocality: 'Hattingen',
          postalCode: '45525',
          addressCountry: 'DE',
          addressRegion: 'North Rhine-Westphalia'
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 51.3989,
          longitude: 7.1864
        },
        priceRange: '€€€',
        starRating: {
          '@type': 'Rating',
          ratingValue: '4',
          bestRating: '5'
        },
        amenityFeature: [
          { '@type': 'LocationFeatureSpecification', name: 'Free WiFi' },
          { '@type': 'LocationFeatureSpecification', name: 'Parking' },
          { '@type': 'LocationFeatureSpecification', name: 'Restaurant' },
          { '@type': 'LocationFeatureSpecification', name: 'Business Center' }
        ],
        hasMap: 'https://maps.google.com/?cid=1234567890'
      },
      {
        '@type': 'Restaurant',
        '@id': `${url}/#restaurant`,
        name: 'Restaurant La Casa',
        description: 'Authentic Italian restaurant serving traditional Mediterranean cuisine with modern flair.',
        url: url,
        telephone: '+49232456650',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Heggerstraße 4',
          addressLocality: 'Hattingen',
          postalCode: '45525',
          addressCountry: 'DE'
        },
        servesCuisine: ['Italian', 'Mediterranean'],
        priceRange: '€€€',
        openingHours: [
          'Mo-Su 18:00-23:00'
        ],
        hasMenu: `${url}/menu`,
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.7',
          reviewCount: '150',
          bestRating: '5'
        }
      },
      {
        '@type': 'WebSite',
        '@id': `${url}/#website`,
        url: url,
        name: siteName,
        description: description,
        publisher: {
          '@id': `${url}/#organization`
        },
        potentialAction: {
          '@type': 'SearchAction',
          target: `${url}/search?q={search_term_string}`,
          'query-input': 'required name=search_term_string'
        }
      },
      {
        '@type': 'Organization',
        '@id': `${url}/#organization`,
        name: siteName,
        url: url,
        logo: {
          '@type': 'ImageObject',
          url: `${url}/logo.png`,
          width: 300,
          height: 100
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+49232456650',
          contactType: 'reservations',
          availableLanguage: ['German', 'English', 'Italian']
        },
        sameAs: [
          'https://www.facebook.com/hausniggemann',
          'https://www.instagram.com/hausniggemann',
          'https://www.tripadvisor.com/hausniggemann'
        ]
      }
    ]
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={url} />
      
      {/* Language and Locale */}
      <html lang="en" />
      <meta httpEquiv="content-language" content="en" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />
      <meta property="twitter:site" content={twitterHandle} />
      <meta property="twitter:creator" content={twitterHandle} />
      
      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="author" content="Haus Niggemann Hotel" />
      <meta name="publisher" content="Haus Niggemann Hotel" />
      <meta name="theme-color" content="#D4AF37" />
      <meta name="msapplication-TileColor" content="#D4AF37" />
      
      {/* Geo Meta Tags */}
      <meta name="geo.region" content="DE-NW" />
      <meta name="geo.placename" content="Hattingen" />
      <meta name="geo.position" content="51.3989;7.1864" />
      <meta name="ICBM" content="51.3989, 7.1864" />
      
      {/* Mobile Optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover" />
      <meta name="format-detection" content="telephone=yes" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      
      {/* Favicon and Icons */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="manifest" href="/manifest.json" />
      
      {/* DNS Prefetch for Performance */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//api.openweathermap.org" />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData, null, 2)}
      </script>
      
      {/* Additional SEO */}
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
      <meta name="revisit-after" content="1 days" />
      
      {/* Business Information */}
      <meta name="business:contact_data:street_address" content="Heggerstraße 4" />
      <meta name="business:contact_data:locality" content="Hattingen" />
      <meta name="business:contact_data:region" content="North Rhine-Westphalia" />
      <meta name="business:contact_data:postal_code" content="45525" />
      <meta name="business:contact_data:country_name" content="Germany" />
      <meta name="business:contact_data:phone_number" content="+49232456650" />
    </Helmet>
  );
};

export default SEOOptimized;